import { addDoc, collection, doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { getCareSourceOriginalText } from './careSourceStatusModel';

const SOURCE_NOTES_COLLECTION = 'careSources';
const TIMELINE_COLLECTION = 'dailyLogs';

const getCurrentUser = () => auth.currentUser || {};

const toDateKey = (value = null) => {
  const date = value instanceof Date ? value : new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return '';

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

export const dismissCareSourceActivity = async (sourceId = '') => {
  const cleanSourceId = String(sourceId || '').trim();
  if (!cleanSourceId) {
    throw new Error('Missing source id.');
  }

  await updateDoc(doc(db, SOURCE_NOTES_COLLECTION, cleanSourceId), {
    sourceActivityDismissedAt: serverTimestamp(),
    sourceActivityDismissedBy: auth.currentUser?.uid || null,
    dismissedByUser: true,
  });
};

export const fetchCareSourceById = async (sourceId = '') => {
  const cleanSourceId = String(sourceId || '').trim();
  if (!cleanSourceId) {
    return null;
  }

  const snapshot = await getDoc(doc(db, SOURCE_NOTES_COLLECTION, cleanSourceId));
  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
};

export const markMedicationSourceReviewComplete = async (sourceId = '', reviewSummary = {}) => {
  const cleanSourceId = String(sourceId || '').trim();
  if (!cleanSourceId) {
    throw new Error('Missing source id.');
  }

  const summary = reviewSummary && typeof reviewSummary === 'object' ? reviewSummary : {};

  await updateDoc(doc(db, SOURCE_NOTES_COLLECTION, cleanSourceId), {
    status: 'reviewed',
    processingStatus: 'reviewed',
    reviewState: 'reviewed',
    reviewLabel: 'Reviewed',
    medicationReviewSummary: {
      schemaVersion: 1,
      reviewedFrom: 'derived_candidate_receipts',
      ...summary,
    },
    medicationReviewCompletedAt: serverTimestamp(),
    medicationReviewCompletedBy: auth.currentUser?.uid || null,
    updatedAt: serverTimestamp(),
    'metadata.captureFirst.status': 'reviewed',
    'metadata.captureFirst.reviewState': 'reviewed',
    'metadata.captureFirst.reviewLabel': 'Reviewed',
    'metadata.careSourceProcessing.status': 'reviewed',
    'metadata.careSourceProcessing.reviewState': 'reviewed',
    'metadata.careSourceProcessing.reviewLabel': 'Reviewed',
  });

  const childId = String(summary.childId || '').trim();
  if (childId) {
    const foundCount = Number(summary.foundCount || 0);
    const resultParts = [
      Number(summary.addedCount || 0) ? `${summary.addedCount} added` : '',
      Number(summary.updatedCount || 0) ? `${summary.updatedCount} updated` : '',
      Number(summary.unchangedCount || 0) ? `${summary.unchangedCount} unchanged` : '',
      Number(summary.notAddedCount || 0) ? `${summary.notAddedCount} not added` : '',
    ].filter(Boolean);
    const text = [
      'Medication list reviewed',
      foundCount ? `${foundCount} medication${foundCount === 1 ? '' : 's'} found` : '',
      resultParts.join(' • '),
    ].filter(Boolean).join(' — ');
    const reviewAnnotationRef = doc(db, TIMELINE_COLLECTION, `medication-review-${cleanSourceId}`);
    try {
      const existingReviewAnnotation = await getDoc(reviewAnnotationRef);
      if (existingReviewAnnotation.exists()) {
        return;
      }
    } catch (error) {
      // A missing document cannot satisfy dailyLogs' resource-based read rule.
      // The following create remains protected by the manager-only write rule.
      if (error?.code !== 'permission-denied') {
        throw error;
      }
    }

    await setDoc(reviewAnnotationRef, {
      childId,
      category: 'source_receipt',
      type: 'medication_review_annotation',
      timelineType: 'medication_review_annotation',
      timelineEvent: false,
      sourceReceiptAnnotation: true,
      title: 'Medication list reviewed',
      text,
      sourceId: cleanSourceId,
      sourceDocument: {
        sourceId: cleanSourceId,
        title: String(summary.sourceTitle || 'Medication document'),
      },
      medicationReviewSummary: summary,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || null,
      status: 'active',
    }, { merge: false });
  }
};

export const markCareKnowledgeSourceReviewComplete = async (sourceId = '', reviewSummary = {}) => {
  const cleanSourceId = String(sourceId || '').trim();
  if (!cleanSourceId) {
    throw new Error('Missing source id.');
  }
  const summary = reviewSummary && typeof reviewSummary === 'object' ? reviewSummary : {};

  await updateDoc(doc(db, SOURCE_NOTES_COLLECTION, cleanSourceId), {
    status: 'reviewed',
    processingStatus: 'reviewed',
    reviewState: 'reviewed',
    reviewLabel: 'Reviewed',
    careKnowledgeReviewCompletedAt: serverTimestamp(),
    careKnowledgeReviewCompletedBy: auth.currentUser?.uid || null,
    ...(Array.isArray(summary.savedItems) ? {
      careKnowledgeSavedItems: summary.savedItems,
    } : {}),
    'careKnowledgeSuggestionSummary.needsReview': false,
    updatedAt: serverTimestamp(),
    'metadata.captureFirst.status': 'reviewed',
    'metadata.captureFirst.reviewState': 'reviewed',
    'metadata.captureFirst.reviewLabel': 'Reviewed',
    'metadata.careSourceProcessing.status': 'reviewed',
    'metadata.careSourceProcessing.reviewState': 'reviewed',
    'metadata.careSourceProcessing.reviewLabel': 'Reviewed',
  });
};

export const saveCareKnowledgeAutoSaveProgress = async (sourceId = '', savedItems = []) => {
  const cleanSourceId = String(sourceId || '').trim();
  if (!cleanSourceId) throw new Error('Missing source id.');

  await updateDoc(doc(db, SOURCE_NOTES_COLLECTION, cleanSourceId), {
    careKnowledgeSavedItems: Array.isArray(savedItems) ? savedItems : [],
    'careKnowledgeSuggestionSummary.needsReview': false,
    updatedAt: serverTimestamp(),
  });
};

export const saveDirectProfileReviewProgress = async (
  sourceId = '',
  { decisions = {}, savedItems = [] } = {}
) => {
  const cleanSourceId = String(sourceId || '').trim();
  if (!cleanSourceId) {
    throw new Error('Missing source id.');
  }

  await updateDoc(doc(db, SOURCE_NOTES_COLLECTION, cleanSourceId), {
    directProfileReviewDecisions: decisions,
    careKnowledgeSavedItems: savedItems,
    directProfileReviewUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const saveCareSourceAsTimelineNote = async (source = {}) => {
  const sourceId = String(source.id || '').trim();
  const childId = String(source.childId || '').trim();
  const originalText = getCareSourceOriginalText(source).trim();

  if (!sourceId) {
    throw new Error('Missing source id.');
  }
  if (!childId || !originalText) {
    throw new Error('Missing saved note details.');
  }

  const currentUser = getCurrentUser();
  const now = serverTimestamp();
  const timestamp = source.selectedDate?.toDate?.()
    || source.sourceMetadata?.selectedDate?.toDate?.()
    || source.createdAt?.toDate?.()
    || source.capturedAt?.toDate?.()
    || new Date();
  const entryDayKey = toDateKey(timestamp);

  const entryRef = await addDoc(collection(db, TIMELINE_COLLECTION), {
    childId,
    childName: source.childName || null,
    collection: TIMELINE_COLLECTION,
    category: 'log',
    logCategory: 'log',
    type: 'log',
    timelineType: 'log',
    title: 'Original note',
    titlePrefix: 'Daily Log',
    text: originalText,
    timestamp,
    timestampUtc: timestamp,
    localDate: entryDayKey,
    entryDayKey,
    sourceNoteId: sourceId,
    sourceSavedAsOriginalNote: true,
    fallbackReason: 'user_saved_original_note',
    createdBy: currentUser.uid || source.createdBy || null,
    authorId: currentUser.uid || source.createdBy || null,
    authorEmail: currentUser.email || null,
    createdAt: now,
    updatedAt: now,
    metadata: {
      needsReview: false,
      sourceNoteId: sourceId,
      savedAsOriginalNote: true,
      fallbackReason: 'user_saved_original_note',
      captureFirst: {
        status: 'saved_as_original_note',
        sourceNoteId: sourceId,
        savedAsOriginalNote: true,
        fallbackReason: 'user_saved_original_note',
      },
    },
  });

  await updateDoc(doc(db, SOURCE_NOTES_COLLECTION, sourceId), {
    status: 'saved_as_original_note',
    processingStatus: 'saved_as_original_note',
    reviewState: 'ready_to_save',
    reviewLabel: 'Saved as one note',
    updatedAt: now,
    extractionSummary: {
      recordCount: 1,
      savedRecordCount: 1,
      failedRecordCount: 0,
      needsReviewCount: 0,
      categoriesFound: ['log'],
      savedAsOriginalNote: true,
      fallbackReason: 'user_saved_original_note',
    },
    'metadata.captureFirst.status': 'saved_as_original_note',
    'metadata.captureFirst.reviewState': 'ready_to_save',
    'metadata.captureFirst.reviewLabel': 'Saved as one note',
    'metadata.captureFirst.savedAsOriginalNote': true,
    'metadata.captureFirst.fallbackReason': 'user_saved_original_note',
    'metadata.captureFirst.timelineEntryIds': [entryRef.id],
    'metadata.careSourceProcessing.status': 'saved_as_original_note',
    'metadata.careSourceProcessing.reviewState': 'ready_to_save',
    'metadata.careSourceProcessing.reviewLabel': 'Saved as one note',
    'metadata.careSourceProcessing.savedAsOriginalNote': true,
    'metadata.careSourceProcessing.fallbackReason': 'user_saved_original_note',
    'metadata.careSourceProcessing.timelineEntryIds': [entryRef.id],
  });

  return {
    ok: true,
    entryId: entryRef.id,
    sourceNoteId: sourceId,
  };
};
